import { Typography } from '@mui/material';
import DocumentBirthForm from 'civil-status/components/document-birth-form';
import { DocumentSearchBirthCrumbs } from 'civil-status/constants/breadcrumb';
import createSchema from 'civil-status/utils/create-schema-birth';
import {
  transformDocumentBirth,
  transformDocumentBirthRequestUpdate,
} from 'civil-status/utils/transform-document-birth';
import { DocumentBirthFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { useFormik } from 'formik';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { DocumentBirthCM } from 'shell/models/document-birth';
import { HttpResponse } from 'shell/models/http-response';

const typeAct = 'N';

export default function DocumentBirthFormPage(): React.ReactElement {
  const params = useParams();

  const [birth] = useHttpFetch<HttpResponse<DocumentBirthCM>, DocumentBirthFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/visualizzazione-atto-nascita/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentBirth,
    }
  );

  const updateBirth = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/modifica-atto-nascita`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const substituteImage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/sostituzione-immagine-atto-nascita`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);
  const { userProfile } = useUserProfile();

  const validationSchema = useMemo(() => {
    return createSchema(mode);
  }, [mode]);

  const formikForm = useFormik<DocumentBirthFormVM>({
    initialValues: (birth.data as DocumentBirthFormVM) || {
      matricola: '',
      surname: '',
      name: '',
      gender: '',
      municipalityBirth: '',
      surnameFather: '',
      nameFather: '',
      surnameMother: '',
      nameMother: '',
      time: null,
      birthday: null,
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      annotationNational: '',
      noteNational: '',
      annotationInternational: '',
      noteInternational: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values: DocumentBirthFormVM, formContext) => {
      // If act substitution mode
      if (mode === PageMode.Substitution) {
        (
          substituteImage({
            data: {
              // TODO MAPPING, to test
              attoNascitaForUpload: {
                numeroAtto: params.numeroAtto || undefined,
                numeroRegistro: values?.registerNumber || undefined,
                parte: params.parte || undefined,
                serie: params.serie1 || undefined,
                anno: params.anno || undefined,
                matricola: values.matricola || undefined,
                cognome: values.surname || undefined,
                nome: values.name || undefined,
                nomePadre: values.nameFather || undefined,
                idOperatore: userProfile.datiLogin?.key?.userName || undefined,
              },
              file: values.uploadFile?.rawTiff?.split(',')[1] || undefined,
              filename: values.uploadFile?.file?.name || undefined,
              mimeType: values.uploadFile?.file?.type || undefined,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue(
            'indicatorUpdate',
            'È necessario effettuare la convalida dei dati.'
          );
        });
      }

      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentBirthRequestUpdate(values);
        (
          updateBirth({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue('indicatorUpdate', '');
        });
      }
    },
  });

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
            },
          },
        });
      }
    };
  }, []);

  // visibility
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    setVisibility(!!formikForm.values?.indicatorUpdate);
  }, [formikForm.values?.indicatorUpdate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Breadcrumbs
        items={[
          ...DocumentSearchBirthCrumbs,
          { name: 'Atto di Nascita', path: '/stato-civile/nascita/ricerca/dettaglio' },
        ]}
        base="stato-civile"
      />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Atto di Nascita {mode === PageMode.International ? '- Annotazioni Internazionali' : ''}
        {mode === PageMode.National ? '- Annotazioni Italia' : ''}
      </Typography>
      {birth.isLoading || birth.pristine || birth.hasErrors ? undefined : (
        <form onSubmit={formikForm.handleSubmit}>
          <SearchMessages
            type={MessageType.Warning}
            title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
            messages={
              formikForm.values?.indicatorUpdate
                ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
                : []
            }
            visibility={visibility}
            onChangeVisibility={() => setVisibility(false)}
          />
          <DocumentBirthForm
            onChanges={onChanges}
            onChange={formikForm.setFieldValue}
            values={formikForm.values}
            errors={formikForm.errors}
            touched={formikForm.touched}
            mode={mode}
            onEditMode={() => {
              setMode(PageMode.Edit);
            }}
            onSubstitutionMode={() => {
              setMode(PageMode.Substitution);
            }}
            onDetailMode={() => {
              setMode(PageMode.Detail);
            }}
            onPrintMode={() => {
              setMode(PageMode.Print);
            }}
            onAnnotationInternationalMode={() => {
              setMode(PageMode.International);
            }}
            onAnnotationNationalMode={() => {
              setMode(PageMode.National);
            }}
            type={typeAct}
          />
        </form>
      )}
    </>
  );
}

DocumentBirthFormPage.displayName = 'home page component';
